// OnboardingWizard - shown once on a brand-new empty tenant.
//
// Steps: welcome -> add your first AI tool -> enable a starter policy -> done.
// On finish (or skip) it calls complete_onboarding so it won't show again.

import { useState } from 'react';
import { Radar, ShieldCheck, Check, ArrowRight, Sparkles, X } from 'lucide-react';
import { useAuth } from '@govern360/shared';
import { completeOnboarding, addFirstTool, addStarterPolicy } from '../lib/onboardingApi.js';

const TOOL_PRESETS = [
  { name: 'ChatGPT', vendor: 'OpenAI' },
  { name: 'Claude', vendor: 'Anthropic' },
  { name: 'GitHub Copilot', vendor: 'GitHub' },
  { name: 'Microsoft Copilot', vendor: 'Microsoft' },
  { name: 'Gemini', vendor: 'Google' },
];

export default function OnboardingWizard({ onClose }) {
  const { tenant } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toolName, setToolName] = useState('');
  const [toolVendor, setToolVendor] = useState('');
  const [toolStatus, setToolStatus] = useState('sanctioned');
  const [addedTool, setAddedTool] = useState(false);
  const [addedPolicy, setAddedPolicy] = useState(false);

  async function finish() {
    setBusy(true);
    await completeOnboarding();
    setBusy(false);
    onClose(true);
  }

  async function skip() {
    setBusy(true);
    await completeOnboarding();
    setBusy(false);
    onClose(false);
  }

  /* G360_K461 - each save used to move on as if it had worked. A refused
     save now stays on its step and says why. */
  const [saveErr, setSaveErr] = useState('');
  async function saveTool() {
    if (!toolName.trim()) { setStep(2); return; }
    setBusy(true); setSaveErr('');
    const r = await addFirstTool(tenant.id, { name: toolName.trim(), vendor: toolVendor.trim(), status: toolStatus });
    setBusy(false);
    if (r && r.error) { setSaveErr('The tool was not added: ' + (r.error.message || 'refused')); return; }
    setAddedTool(true);
    setStep(2);
  }

  async function savePolicy() {
    setBusy(true); setSaveErr('');
    const r = await addStarterPolicy(tenant.id);
    setBusy(false);
    if (r && r.error) { setSaveErr('The policy was not enabled: ' + (r.error.message || 'refused')); return; }
    setAddedPolicy(true);
    setStep(3);
  }

  return (
    <div className="wiz-backdrop">
      <div className="wiz-card">
        <button className="wiz-x" onClick={skip} aria-label="Skip setup"><X size={18} /></button>

        {/* Progress dots */}
        <div className="wiz-dots">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`wiz-dot ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="wiz-body">
            <div className="wiz-icon"><Sparkles size={28} /></div>
            <h2 className="wiz-h">Welcome to Govern360</h2>
            <p className="wiz-p">Let&apos;s get {tenant?.name || 'your workspace'} set up in three quick steps: add an AI tool you use, switch on a starter policy, and you&apos;re governing. Takes about a minute.</p>
            <button className="btn primary block" onClick={() => setStep(1)}>Get started<ArrowRight size={18} /></button>
            <button className="wiz-skip" onClick={skip}>Skip for now</button>
          </div>
        )}

        {step === 1 && (
          <div className="wiz-body">
            <div className="wiz-icon"><Radar size={28} /></div>
            <h2 className="wiz-h">Add your first AI tool</h2>
            <p className="wiz-p">What&apos;s an AI tool your team uses? You can add the rest later, or discover them automatically.</p>

            <div className="wiz-presets">
              {TOOL_PRESETS.map((p) => (
                <button key={p.name}
                  className={`wiz-preset ${toolName === p.name ? 'sel' : ''}`}
                  onClick={() => { setToolName(p.name); setToolVendor(p.vendor); }}>
                  {p.name}
                </button>
              ))}
            </div>

            <label className="field-l">Tool name</label>
            <input className="inp" value={toolName} onChange={(e) => setToolName(e.target.value)} placeholder="e.g. ChatGPT" />
            <label className="field-l" style={{ marginTop: 10 }}>Status</label>
            <select className="inp" value={toolStatus} onChange={(e) => setToolStatus(e.target.value)}>
              <option value="sanctioned">Sanctioned (approved)</option>
              <option value="review">Under review</option>
              <option value="shadow">Shadow (unapproved)</option>
            </select>

            <button className="btn primary block" style={{ marginTop: 16 }} onClick={saveTool} disabled={busy}>
              {toolName.trim() ? 'Add tool & continue' : 'Skip this step'}<ArrowRight size={18} />
            </button>
            {saveErr && <div className="g360-onb-err" style={{ fontSize: 12, color: 'var(--g-danger)', marginTop: 10 }}>{saveErr}</div>}
          </div>
        )}

        {step === 2 && (
          <div className="wiz-body">
            <div className="wiz-icon"><ShieldCheck size={28} /></div>
            <h2 className="wiz-h">Turn on a starter policy</h2>
            <p className="wiz-p">We&apos;ll enable <strong>&ldquo;Block secrets to any AI&rdquo;</strong> &mdash; it stops API keys, tokens, and private keys from being pasted into any AI tool. The single highest-value rule to start with.</p>
            <div className="wiz-policy-preview">
              <ShieldCheck size={18} style={{ color: 'var(--g-danger)' }} />
              <div>
                <div style={{ fontWeight: 500, fontSize: 13 }}>Block secrets to any AI</div>
                <div style={{ fontSize: 12, color: 'var(--g-text-2)' }}>AWS keys &middot; GitHub tokens &middot; private keys &middot; OpenAI keys</div>
              </div>
              <span className="pill p-dang" style={{ marginLeft: 'auto' }}>Block</span>
            </div>
            <button className="btn primary block" style={{ marginTop: 16 }} onClick={savePolicy} disabled={busy}>
              Enable policy & continue<ArrowRight size={18} />
            </button>
            {saveErr && <div className="g360-onb-err" style={{ fontSize: 12, color: 'var(--g-danger)', marginTop: 10 }}>{saveErr}</div>}
            <button className="wiz-skip" onClick={() => setStep(3)}>Skip this step</button>
          </div>
        )}

        {step === 3 && (
          <div className="wiz-body">
            <div className="wiz-icon done"><Check size={28} /></div>
            <h2 className="wiz-h">You&apos;re set up</h2>
            <p className="wiz-p">
              {addedTool && 'Your first tool is in the inventory. '}
              {addedPolicy && 'Your starter policy is live. '}
              From here, run a discovery scan to find shadow AI, add more policies, and explore your compliance posture.
            </p>
            <div className="wiz-next">
              <div className="wiz-next-item"><Radar size={16} /> Add or discover more AI tools</div>
              <div className="wiz-next-item"><ShieldCheck size={16} /> Build out Data Shield detectors</div>
              <div className="wiz-next-item"><Check size={16} /> Enable compliance frameworks</div>
            </div>
            <button className="btn primary block" onClick={finish} disabled={busy}>Go to dashboard</button>
          </div>
        )}
      </div>
    </div>
  );
}
